import React from 'react';

/**
 * Error Boundary Component
 * Catches JavaScript errors anywhere in child component tree
 * Prevents white screen crashes and shows friendly error message
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null,
      errorInfo: null 
    };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    this.setState({
      error,
      errorInfo
    });
  }

  handleReset = () => {
    this.setState({ 
      hasError: false, 
      error: null,
      errorInfo: null 
    });
  };

  render() {
    if (this.state.hasError) {
      return (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '400px',
          padding: '40px 20px',
          textAlign: 'center',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          borderRadius: '16px',
          margin: '20px',
          color: 'white'
        }}>
          <div style={{ 
            fontSize: '64px', 
            marginBottom: '20px',
            animation: 'bounce 1s infinite'
          }}>
            ⚠️
          </div>
          <h2 style={{ 
            fontSize: '28px', 
            fontWeight: 'bold', 
            marginBottom: '15px',
            textShadow: '2px 2px 4px rgba(0,0,0,0.2)'
          }}>
            Ups! Coś poszło nie tak
          </h2>
          <p style={{ 
            fontSize: '16px', 
            marginBottom: '30px',
            maxWidth: '600px',
            lineHeight: '1.6',
            opacity: 0.95
          }}>
            {this.props.fallbackMessage || 
              'Wystąpił nieoczekiwany błąd w tej sekcji aplikacji. Spróbuj odświeżyć stronę lub skontaktuj się z administratorem.'}
          </p>
          
          <div style={{ 
            display: 'flex', 
            gap: '15px',
            flexWrap: 'wrap',
            justifyContent: 'center'
          }}>
            <button
              onClick={this.handleReset}
              style={{
                padding: '12px 30px',
                fontSize: '16px',
                fontWeight: 'bold',
                border: '2px solid white',
                borderRadius: '8px',
                background: 'rgba(255, 255, 255, 0.2)',
                color: 'white',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                backdropFilter: 'blur(10px)'
              }}
              onMouseEnter={(e) => {
                e.target.style.background = 'rgba(255, 255, 255, 0.3)';
                e.target.style.transform = 'translateY(-2px)';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = 'rgba(255, 255, 255, 0.2)';
                e.target.style.transform = 'translateY(0)';
              }}
            >
              🔄 Spróbuj ponownie
            </button>
            
            <button
              onClick={() => window.location.reload()}
              style={{
                padding: '12px 30px',
                fontSize: '16px',
                fontWeight: 'bold',
                border: '2px solid white',
                borderRadius: '8px',
                background: 'white',
                color: '#667eea',
                cursor: 'pointer',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 5px 15px rgba(0,0,0,0.3)';
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = 'none';
              }}
            >
              ↻ Odśwież stronę
            </button>
          </div>

          {process.env.NODE_ENV === 'development' && this.state.error && (
            <details style={{
              marginTop: '30px',
              padding: '20px',
              background: 'rgba(0, 0, 0, 0.3)',
              borderRadius: '8px',
              textAlign: 'left',
              maxWidth: '800px',
              width: '100%',
              fontSize: '13px',
              fontFamily: 'monospace'
            }}>
              <summary style={{ 
                cursor: 'pointer', 
                fontWeight: 'bold',
                marginBottom: '10px',
                fontSize: '14px'
              }}>
                🐛 Szczegóły błędu (tryb deweloperski)
              </summary>
              <pre style={{ 
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                lineHeight: '1.4'
              }}>
                {this.state.error.toString()}
                {this.state.errorInfo && this.state.errorInfo.componentStack}
              </pre>
            </details>
          )}
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
